import { Center, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { flatten } from '@turf/turf';
import { Feature, Point } from 'geojson';
import { MapMouseEvent } from 'mapbox-gl';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef, Popup } from 'react-map-gl';

import { FieldMapsLayout } from '../../../../components/field-maps-layout/field-maps-layout';
import { SelectMapType } from '../../../../components/select-map-type/select-map-type';
import UploadSamplesMapForm from '../../../../components/upload-samples-map-form/upload-samples-map-form';
import { useSharedFieldMaps } from '../../../../hooks/use-shared-field-maps/shared-field-maps';
import { MAP_DISPLAY_TYPES } from '../compare-maps/page';

import { FORM_STATUS } from '@advices/data-access';
import { useAuth } from '@auth/data-access';
import { hasAdminAccess } from '@data-access';
import { fieldMapsApi } from '@fields/data-access';
import {
  LegendControl,
  PfMap,
  SAMPLES_LAYER_ID,
  SamplesLayers,
  ZoningLegend,
  ZoningLegendProps,
} from '@map';
import { ActionsMenu, ActionsMenuItem, Drawer } from '@ui';

interface SamplesMapProps {
  type: MAP_DISPLAY_TYPES;
}

export function SamplesMap({ type }: SamplesMapProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [opened, actions] = useDisclosure(false);

  const { field, fieldId, compare, queries, handleCompareChange } =
    useSharedFieldMaps({ type });
  const [selectedPoint, setSelectedPoint] = useState<Feature<Point> | null>(
    null,
  );
  const mapRef = useRef<MapRef>(null);

  const summary = useSuspenseQuery(queries.samplesSummary(Number(fieldId)));
  const [selectedMap, setSelectedMap] = useState<string | null>(
    summary.data.options[0]?.value ?? null,
  );
  const [selectedCol, setSelectedCol] = useState<string>(
    summary.data.options[0]?.data.cols[0],
  );
  const map = useQuery({
    ...queries.samplesMap({
      mapId: Number(selectedMap),
      col: selectedCol,
    }),
    enabled: selectedMap !== null,
  });
  const deleteMap = useMutation({
    mutationFn: fieldMapsApi.deleteSamplesMap,
  });

  const menuActions: ActionsMenuItem[] = [
    {
      type: 'action',
      label: t('common.add-map'),
      icon: IconPlus,
      onClick: actions.open,
      hasAccess: hasAdminAccess(user?.user_access),
    },
    {
      type: 'action',
      label: t('common.delete'),
      icon: IconTrash,
      color: 'red',
      onClick() {
        modals.openConfirmModal({
          title: t('common.delete-map'),
          children: t('common.delete-map-confirm'),
          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
          confirmProps: { color: 'red' },
          onConfirm: () => deleteMap.mutate(Number(selectedMap)),
        });
      },
    },
  ];

  const handleMapCursor = (e: MapMouseEvent) => {
    if (e.type === 'mouseenter') {
      e.target.getCanvas().style.cursor = 'pointer';
    }

    if (e.type === 'mouseleave') {
      e.target.getCanvas().style.cursor = '';
    }

    if (e.type === 'click' && e.features?.length) {
      setSelectedPoint(flatten(e.features[0]).features[0]);
    }
  };

  const legend = useMemo((): ZoningLegendProps => {
    if (map.data) {
      const l = map.data.map.features.reduce((acc, feature) => {
        return {
          ...acc,
          [feature.properties.zone]: {
            zone: feature.properties.zone,
            color: feature.properties.color,
            value: feature.properties.value,
          },
        };
      }, {});

      const unit = map.data?.unit;

      return {
        title: selectedCol,
        legend: Object.values(l),
        unit: unit ?? '',
      };
    }
    return {
      legend: [],
    };
  }, [map.data, selectedCol]);

  return (
    <>
      <FieldMapsLayout
        content={
          <>
            {map.data?.status === FORM_STATUS.FINISHED ? (
              <PfMap
                interactiveLayerIds={[SAMPLES_LAYER_ID]}
                onClick={handleMapCursor}
                onMouseDown={handleMapCursor}
                onMouseEnter={handleMapCursor}
                ref={mapRef}
                zoomToGeojson={field.data}
              >
                {map.data?.map ? <SamplesLayers data={map.data.map} /> : null}

                <LegendControl>
                  <ZoningLegend {...legend} />
                </LegendControl>

                {selectedPoint && (
                  <Popup
                    closeOnClick={false}
                    latitude={selectedPoint.geometry.coordinates[1]}
                    longitude={selectedPoint.geometry.coordinates[0]}
                    onClose={() => setSelectedPoint(null)}
                  >
                    <Text>{t('common.information-du-point')}</Text>
                    <Text mt={5}>
                      {selectedCol} : {selectedPoint.properties?.value}
                    </Text>
                  </Popup>
                )}
              </PfMap>
            ) : null}

            {summary.data?.options.length === 0 ? (
              <Center h="100%" p="xl">
                <Stack>
                  <Text fw="bold" size="xl" ta="center">
                    {t('common.no-samples-map-available')}
                  </Text>
                </Stack>
              </Center>
            ) : null}
          </>
        }
        header={
          <Group h="100%" justify="space-between" p="md">
            <Group>
              {type !== 'single' ? (
                <SelectMapType
                  maps={field.data.properties.maps}
                  onChange={handleCompareChange}
                  value={type === 'compare-left' ? compare?.[0] : compare?.[1]}
                />
              ) : null}
              {summary.data?.options.length ? (
                <>
                  <Select
                    allowDeselect={false}
                    data={summary.data?.options}
                    disabled={summary.data?.options.length === 0}
                    onChange={setSelectedMap}
                    rightSection={map.isFetching ? <Loader size="xs" /> : null}
                    value={selectedMap}
                  />
                  <Select
                    allowDeselect={false}
                    data={
                      summary.data?.options.find(
                        (item) => item.value === selectedMap,
                      )?.data.cols
                    }
                    onChange={(value) => setSelectedCol(value!)}
                    value={selectedCol}
                  />
                </>
              ) : null}
            </Group>
            <Group>
              <ActionsMenu items={menuActions}>
                <ActionsMenu.Button />
              </ActionsMenu>
            </Group>
          </Group>
        }
      />
      <Drawer onClose={actions.close} opened={opened} position="right">
        <UploadSamplesMapForm
          fieldId={Number(fieldId)}
          onClose={actions.close}
        />
      </Drawer>
    </>
  );
}

export default SamplesMap;
